import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Send, Users2, Mail, Eye, Loader2, FileText, X, Paperclip, Upload } from "lucide-react";
import RichTextEditor from "./RichTextEditor";
import { useAuth } from "../context/AuthContext";

const DAY_LABEL = { monday: "Lundi", tuesday: "Mardi", wednesday: "Mercredi", thursday: "Jeudi", friday: "Vendredi", saturday: "Samedi", sunday: "Dimanche" };
const DAY_ORDER = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

const BroadcastAdmin = () => {
  const { user } = useAuth();
  const restricted = !!user?.restricted;
  const teacherId = user?.teacherId;
  const [slots, setSlots] = useState([]);
  const [selected, setSelected] = useState([]);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [previewList, setPreviewList] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [mailPreview, setMailPreview] = useState(null);
  const [loadingMailPreview, setLoadingMailPreview] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (file.size > 15 * 1024 * 1024) { toast.error(`${file.name} : trop volumineux (max 15 Mo)`); continue; }
        const form = new FormData();
        form.append("file", file);
        const { data } = await api.post("/admin/upload-document", form, { headers: { "Content-Type": "multipart/form-data" } });
        setAttachments((prev) => [...prev, { url: data.url, filename: data.filename, size: data.size }]);
      }
      toast.success("Pièce(s) jointe(s) ajoutée(s)");
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur lors du téléversement"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const totalAttachSize = useMemo(() => attachments.reduce((s, a) => s + (a.size || 0), 0), [attachments]);

  const plainMessage = useMemo(() => message.replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ").trim(), [message]);
  const isPersonalised = useMemo(() => /\{(prenom_parent|nom_parent|eleves|cours)\}/.test(message), [message]);

  useEffect(() => {
    api.get("/content").then(({ data }) => {
      const all = data.scheduleSlots || [];
      setSlots(restricted && teacherId ? all.filter((s) => s.teacherId === teacherId) : all);
    });
  }, [restricted, teacherId]);

  const grouped = useMemo(() => {
    const g = {};
    for (const s of slots) {
      if (!g[s.day]) g[s.day] = [];
      g[s.day].push(s);
    }
    for (const d of Object.keys(g)) g[d].sort((a, b) => (a.startTime || "").localeCompare(b.startTime || ""));
    return g;
  }, [slots]);

  const toggle = (id) => setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const selectAll = () => setSelected(slots.map((s) => s.id));
  const clearAll = () => setSelected([]);

  const doPreview = async () => {
    if (selected.length === 0) { toast.error("Sélectionnez au moins un créneau"); return; }
    setLoadingPreview(true);
    try {
      const { data } = await api.post("/admin/broadcast", { slotIds: selected, subject: subject || "Preview", message: message || "Preview", preview: true });
      setPreviewList(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setLoadingPreview(false); }
  };

  const doMailPreview = async () => {
    if (!plainMessage) { toast.error("Message requis"); return; }
    setLoadingMailPreview(true);
    try {
      const { data } = await api.post("/admin/broadcast/render-preview", { subject, message });
      setMailPreview(data.html);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setLoadingMailPreview(false); }
  };

  const send = async () => {
    if (selected.length === 0) { toast.error("Sélectionnez au moins un créneau"); return; }
    if (!subject.trim()) { toast.error("Objet requis"); return; }
    if (!plainMessage) { toast.error("Message requis"); return; }
    const recipientCount = previewList?.recipients?.length ?? "?";
    const mode = isPersonalised
      ? `à ${recipientCount} famille(s) en emails individuels personnalisés`
      : `à ${recipientCount} famille(s) en copie cachée (un seul email)`;
    if (!window.confirm(`Envoyer ce message ${mode} ?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/broadcast", { slotIds: selected, subject, message, attachments });
      const batchInfo = data.batches > 1 ? ` en ${data.batches} lot(s)` : "";
      toast.success(`${data.sent} email(s) envoyé(s)${batchInfo}${data.failed ? ` · ${data.failed} échec(s)` : ""}`);
      setPreviewList(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="broadcast-admin">
      <div className="mb-5">
        <h1 className="font-serif-display text-2xl sm:text-3xl text-stone-800 flex items-center gap-2"><Mail size={24}/> Envoi de mails aux familles</h1>
        <p className="text-stone-500 mt-1 text-sm sm:text-base">Sélectionnez un ou plusieurs créneaux du planning. Un <strong>seul email</strong> est envoyé, toutes les familles étant mises en <strong>copie cachée (BCC)</strong> — elles ne voient donc pas les adresses des autres. Au-delà de 90 destinataires, l'envoi est automatiquement découpé en plusieurs lots.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Slot picker */}
        <div className="lg:col-span-1 bg-white rounded-2xl shadow-sm p-4 sm:p-5">
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <h2 className="font-serif-display text-lg text-stone-800">Créneaux ({selected.length})</h2>
            <div className="flex gap-2">
              <button onClick={selectAll} className="text-xs px-2 py-1 rounded bg-stone-100 hover:bg-stone-200" data-testid="broadcast-select-all">Tout</button>
              <button onClick={clearAll} className="text-xs px-2 py-1 rounded bg-stone-100 hover:bg-stone-200" data-testid="broadcast-clear-all">Aucun</button>
            </div>
          </div>
          <div className="space-y-3 max-h-[600px] overflow-y-auto pr-1">
            {DAY_ORDER.filter((d) => grouped[d]?.length).map((d) => (
              <div key={d}>
                <div className="text-xs uppercase tracking-widest text-deep-rose font-medium mb-1.5">{DAY_LABEL[d]}</div>
                <div className="space-y-1">
                  {grouped[d].map((s) => {
                    const checked = selected.includes(s.id);
                    return (
                      <label key={s.id} className={`flex items-start gap-2 p-2 rounded-lg cursor-pointer text-sm ${checked ? "bg-blush-light border border-deep-rose" : "border border-stone-200 hover:border-blush"}`} data-testid={`broadcast-slot-${s.id}`}>
                        <input type="checkbox" checked={checked} onChange={() => toggle(s.id)} className="mt-1 accent-deep-rose" />
                        <div className="min-w-0 flex-1">
                          <div className="text-stone-800">{s.startTime}–{s.endTime} · {s.title}</div>
                          <div className="text-stone-500 text-xs">{s.teacherName || "—"}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Editor */}
        <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm p-4 sm:p-5">
          <h2 className="font-serif-display text-lg text-stone-800 mb-3">Message</h2>
          <div className="space-y-3">
            <div>
              <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1.5">Objet *</label>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="broadcast-subject" placeholder="Rappel spectacle de fin d'année…" className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blush" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1.5">Message *</label>
              <RichTextEditor value={message} onChange={setMessage} testId="broadcast-editor" />
              <div className="text-stone-500 text-xs mt-1.5 space-y-1">
                <div>Un bandeau automatique « Concerne : nom des créneaux » sera ajouté sous votre message.</div>
                {isPersonalised ? (
                  <div className="text-deep-rose">Variables détectées : l'envoi se fera en <strong>emails individuels personnalisés</strong> (une famille par email).</div>
                ) : (
                  <div>Sans variable, un <strong>seul email</strong> est envoyé avec toutes les familles en copie cachée.</div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1.5 flex items-center gap-1.5"><Paperclip size={13} /> Pièces jointes</label>
              <input ref={fileRef} type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp" onChange={onFiles} className="hidden" data-testid="broadcast-attach-input" />
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blush-light text-deep-rose text-xs font-medium disabled:opacity-50" data-testid="broadcast-attach-btn">
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {uploading ? "Envoi…" : "Joindre un fichier"}
              </button>
              {attachments.length > 0 && (
                <div className="mt-2 space-y-1.5" data-testid="broadcast-attachments">
                  {attachments.map((a, i) => (
                    <div key={a.url} className="flex items-center gap-2 bg-stone-50 border border-stone-200 rounded-lg px-3 py-1.5 text-sm">
                      <FileText size={14} className="text-deep-rose shrink-0" />
                      <span className="truncate flex-1 text-stone-700">{a.filename}</span>
                      <span className="text-xs text-stone-400 shrink-0">{Math.round((a.size || 0) / 1024)} Ko</span>
                      <button type="button" onClick={() => setAttachments((prev) => prev.filter((_, j) => j !== i))} className="p-1 rounded hover:bg-stone-200" data-testid={`broadcast-attach-remove-${i}`}><X size={13} /></button>
                    </div>
                  ))}
                  <div className="text-[11px] text-stone-400">Total : {Math.round(totalAttachSize / 1024)} Ko (18 Mo maximum). PDF, Word, Excel ou image.</div>
                </div>
              )}
            </div>

            <div className="flex gap-3 flex-wrap">
              <button onClick={doPreview} disabled={loadingPreview || selected.length === 0} className="inline-flex items-center gap-2 bg-stone-100 hover:bg-stone-200 text-stone-700 px-4 py-2 rounded-full text-sm disabled:opacity-50" data-testid="broadcast-preview-btn">
                {loadingPreview ? <Loader2 className="animate-spin" size={14}/> : <Eye size={14}/>} Prévisualiser destinataires
              </button>
              <button onClick={doMailPreview} disabled={loadingMailPreview || !plainMessage} className="inline-flex items-center gap-2 bg-stone-100 hover:bg-stone-200 text-stone-700 px-4 py-2 rounded-full text-sm disabled:opacity-50" data-testid="broadcast-mail-preview-btn">
                {loadingMailPreview ? <Loader2 className="animate-spin" size={14}/> : <FileText size={14}/>} Aperçu du mail
              </button>
              <button onClick={send} disabled={sending} className="inline-flex items-center gap-2 bg-coral text-white px-5 py-2 rounded-full text-sm disabled:opacity-50" data-testid="broadcast-send-btn">
                {sending ? <Loader2 className="animate-spin" size={14}/> : <Send size={14}/>} Envoyer
              </button>
            </div>
          </div>

          {previewList && (
            <div className="mt-5 border-t border-stone-100 pt-4">
              <h3 className="font-serif-display text-stone-800 mb-2 flex items-center gap-2"><Users2 size={16}/> {previewList.recipients.length} famille(s) — {previewList.totalEnrollments} inscription(s)</h3>
              {previewList.recipients.length === 0 ? (
                <div className="text-stone-500 text-sm italic">Aucun destinataire (aucune famille inscrite à ces créneaux).</div>
              ) : (
                <div className="space-y-1.5 max-h-[300px] overflow-y-auto" data-testid="broadcast-preview-list">
                  {previewList.recipients.map((r) => (
                    <div key={r.email} className="bg-stone-50 rounded-lg px-3 py-2 text-sm">
                      <div className="text-stone-800">{r.parentName || "(sans nom)"} — <a href={`mailto:${r.email}`} className="text-deep-rose">{r.email}</a></div>
                      <div className="text-stone-500 text-xs">Élève(s) : {r.students.join(", ") || "—"} · Cours : {r.slots.join(", ") || "—"}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {mailPreview && (
        <div className="fixed inset-0 z-50 bg-stone-900/60 flex items-start sm:items-center justify-center p-2 sm:p-6 overflow-y-auto" onClick={() => setMailPreview(null)} data-testid="broadcast-mail-preview-modal">
          <div className="bg-white rounded-2xl w-full max-w-2xl overflow-hidden my-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100">
              <h3 className="font-serif-display text-stone-800">Aperçu du mail</h3>
              <button onClick={() => setMailPreview(null)} className="p-1.5 rounded-lg hover:bg-stone-100" data-testid="broadcast-mail-preview-close"><X size={18}/></button>
            </div>
            <iframe title="Aperçu du mail" srcDoc={mailPreview} className="w-full h-[70vh] border-0" />
          </div>
        </div>
      )}
    </div>
  );
};

export default BroadcastAdmin;
